import { useState, useEffect, useCallback, useRef } from 'react';

export const TOUR_STORAGE_KEY = 'wb_onboarding_v1_done';

type TooltipSide = 'below' | 'above' | 'right' | 'center';

type StepDef = {
  id: string;
  target: string | null;
  title: string;
  desc: string;
  icon: string;
  tooltipSide: TooltipSide;
};

const STEPS: StepDef[] = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to Website Builder',
    desc: "Find and launch your perfect website template in seconds. Let's take a quick 30-second tour to show you the highlights.",
    icon: 'ri-rocket-2-line',
    tooltipSide: 'center',
  },
  {
    id: 'search',
    target: 'search',
    title: 'Search & AI Mode',
    desc: 'Type keywords to search, or flip the AI toggle to describe your dream site in plain English — AI finds the best matches instantly.',
    icon: 'ri-search-2-line',
    tooltipSide: 'below',
  },
  {
    id: 'ai-card',
    target: 'ai-card',
    title: 'Start from Scratch with AI',
    desc: 'No idea where to begin? Describe your website and our AI recommends the perfect template from 335+ options. Zero guesswork.',
    icon: 'ri-sparkling-2-fill',
    tooltipSide: 'right',
  },
  {
    id: 'collections',
    target: 'collections',
    title: 'Curated Collections',
    desc: 'Hand-picked template packs organized by industry and style. One click filters the entire grid to matching templates.',
    icon: 'ri-collection-fill',
    tooltipSide: 'above',
  },
];

const PAD = 14;
const TOOLTIP_W = 308;
const TOOLTIP_GAP = 18;

type SpotRect = { left: number; top: number; width: number; height: number };

interface Props {
  onDone: () => void;
}

/* ── CSS keyframes injected once ── */
const STYLE_ID = 'wb-tour-keyframes';
function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes wbSpotPing {
      0%   { transform: scale(1);   opacity: 0.6; }
      100% { transform: scale(1.15); opacity: 0; }
    }
    @keyframes wbSpotBeam {
      0%, 100% { opacity: 0.5; }
      50%       { opacity: 1; }
    }
    @keyframes wbTooltipIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes wbOverlayIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

/* ── Corner brackets around spotlight ── */
function CornerBrackets({ rect }: { rect: SpotRect }) {
  const sx = rect.left - PAD;
  const sy = rect.top  - PAD;
  const sw = rect.width  + PAD * 2;
  const sh = rect.height + PAD * 2;
  const SZ = 14; // bracket arm length
  const T  = 2;  // thickness
  const C  = 'rgba(139,92,246,0.9)';

  const corners = [
    { x: sx,      y: sy,      rx: 0, ry: 0 },
    { x: sx + sw, y: sy,      rx: SZ + T, ry: 0 },
    { x: sx,      y: sy + sh, rx: 0, ry: SZ + T },
    { x: sx + sw, y: sy + sh, rx: SZ + T, ry: SZ + T },
  ];

  return (
    <>
      {corners.map(({ x, y, rx, ry }, i) => {
        const flipX = rx > 0;
        const flipY = ry > 0;
        return (
          <div
            key={i}
            style={{
              position: 'fixed',
              left: x - (flipX ? SZ + T : 0),
              top:  y - (flipY ? SZ + T : 0),
              width:  SZ + T,
              height: SZ + T,
              zIndex: 10001,
              pointerEvents: 'none',
            }}
          >
            {/* Horizontal arm */}
            <div style={{
              position: 'absolute',
              left:   flipX ? 0 : 0,
              right:  flipX ? undefined : 0,
              top:    flipY ? undefined : 0,
              bottom: flipY ? 0 : undefined,
              width: SZ,
              height: T,
              background: C,
              borderRadius: T,
            }} />
            {/* Vertical arm */}
            <div style={{
              position: 'absolute',
              left:   flipX ? undefined : 0,
              right:  flipX ? 0 : undefined,
              top:    flipY ? undefined : 0,
              bottom: flipY ? 0 : undefined,
              width: T,
              height: SZ,
              background: C,
              borderRadius: T,
            }} />
          </div>
        );
      })}
    </>
  );
}

/* ── Tooltip arrow caret ── */
function Caret({ side }: { side: TooltipSide }) {
  if (side === 'center') return null;

  const base: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
    pointerEvents: 'none',
  };

  if (side === 'below') return (
    <div style={{
      ...base,
      top: -8,
      left: '50%',
      transform: 'translateX(-50%)',
      borderWidth: '0 8px 8px 8px',
      borderColor: 'transparent transparent #111827 transparent',
    }} />
  );
  if (side === 'above') return (
    <div style={{
      ...base,
      bottom: -8,
      left: '50%',
      transform: 'translateX(-50%)',
      borderWidth: '8px 8px 0 8px',
      borderColor: '#111827 transparent transparent transparent',
    }} />
  );
  if (side === 'right') return (
    <div style={{
      ...base,
      top: '50%',
      left: -8,
      transform: 'translateY(-50%)',
      borderWidth: '8px 8px 8px 0',
      borderColor: 'transparent #111827 transparent transparent',
    }} />
  );
  return null;
}

/* ── Main component ── */
export default function OnboardingTour({ onDone }: Props) {
  const [stepIndex, setStepIndex] = useState(0);
  const [spotRect, setSpotRect] = useState<SpotRect | null>(null);
  const [ready, setReady] = useState(false); // spotlight has been measured
  const measuringRef = useRef(false);

  useEffect(() => { ensureKeyframes(); }, []);

  // Measure target + scroll into view
  const measureTarget = useCallback((target: string | null) => {
    if (!target) {
      setSpotRect(null);
      setReady(true);
      return;
    }
    const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
    if (!el) { setSpotRect(null); setReady(true); return; }

    setReady(false);
    measuringRef.current = true;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const t = setTimeout(() => {
      if (!measuringRef.current) return;
      const r = el.getBoundingClientRect();
      setSpotRect({ left: r.left, top: r.top, width: r.width, height: r.height });
      setReady(true);
      measuringRef.current = false;
    }, 550);

    return () => { clearTimeout(t); measuringRef.current = false; };
  }, []);

  useEffect(() => {
    const cleanup = measureTarget(STEPS[stepIndex].target);
    return cleanup;
  }, [stepIndex, measureTarget]);

  const handleNext = () => {
    if (stepIndex < STEPS.length - 1) {
      setReady(false);
      setStepIndex((i) => i + 1);
    } else {
      handleDone();
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setReady(false);
      setStepIndex((i) => i - 1);
    }
  };

  const handleDone = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
    onDone();
  }, [onDone]);

  const currentStep = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  const isWelcome = currentStep.target === null;

  // ── Spotlight geometry ──
  const sx = spotRect ? spotRect.left - PAD : 0;
  const sy = spotRect ? spotRect.top  - PAD : 0;
  const sw = spotRect ? spotRect.width  + PAD * 2 : 0;
  const sh = spotRect ? spotRect.height + PAD * 2 : 0;

  // ── Tooltip position ──
  const getTooltipStyle = (): React.CSSProperties => {
    if (!spotRect || isWelcome) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: TOOLTIP_W,
        zIndex: 10002,
        animation: 'wbTooltipIn 0.28s ease both',
      };
    }

    let style: React.CSSProperties = { position: 'fixed', width: TOOLTIP_W, zIndex: 10002, animation: 'wbTooltipIn 0.28s ease both' };

    const side = currentStep.tooltipSide;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (side === 'below') {
      const rawLeft = sx + sw / 2 - TOOLTIP_W / 2;
      style.top  = sy + sh + TOOLTIP_GAP;
      style.left = Math.max(16, Math.min(vw - TOOLTIP_W - 16, rawLeft));
    } else if (side === 'above') {
      const rawLeft = sx + sw / 2 - TOOLTIP_W / 2;
      style.bottom = vh - sy + TOOLTIP_GAP;
      style.left   = Math.max(16, Math.min(vw - TOOLTIP_W - 16, rawLeft));
    } else if (side === 'right') {
      const rawTop = sy + sh / 2 - 120;
      style.left = sx + sw + TOOLTIP_GAP;
      style.top  = Math.max(16, Math.min(vh - 260, rawTop));
      // If too far right, flip to left
      if (sx + sw + TOOLTIP_GAP + TOOLTIP_W > vw - 16) {
        style.left = Math.max(16, sx - TOOLTIP_GAP - TOOLTIP_W);
      }
    }

    return style;
  };

  return (
    <>
      {/* ── Full-screen click blocker (behind spotlight) ── */}
      <div
        style={{
          position: 'fixed', inset: 0,
          zIndex: 9996,
          cursor: 'default',
          animation: 'wbOverlayIn 0.3s ease both',
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* ── Dark backdrop (welcome = full dark, spotlight = transparent so box-shadow provides dark) ── */}
      {isWelcome && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.78)',
            zIndex: 9997,
            animation: 'wbOverlayIn 0.3s ease both',
          }}
        />
      )}

      {/* ── Spotlight (box-shadow creates the dark surround) ── */}
      {!isWelcome && ready && spotRect && (
        <>
          {/* Main spotlight hole */}
          <div
            style={{
              position: 'fixed',
              left: sx,
              top: sy,
              width: sw,
              height: sh,
              borderRadius: 12,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.78)',
              zIndex: 9997,
              pointerEvents: 'none',
              transition: 'left 0.4s cubic-bezier(0.4,0,0.2,1), top 0.4s cubic-bezier(0.4,0,0.2,1), width 0.4s cubic-bezier(0.4,0,0.2,1), height 0.4s cubic-bezier(0.4,0,0.2,1)',
            }}
          />

          {/* Glow border */}
          <div
            style={{
              position: 'fixed',
              left: sx,
              top: sy,
              width: sw,
              height: sh,
              borderRadius: 12,
              border: '2px solid rgba(139,92,246,0.85)',
              zIndex: 9999,
              pointerEvents: 'none',
              animation: 'wbSpotBeam 2s ease-in-out infinite',
              transition: 'left 0.4s cubic-bezier(0.4,0,0.2,1), top 0.4s cubic-bezier(0.4,0,0.2,1), width 0.4s cubic-bezier(0.4,0,0.2,1), height 0.4s cubic-bezier(0.4,0,0.2,1)',
            }}
          />

          {/* Pulse ring 1 */}
          <div
            style={{
              position: 'fixed',
              left: sx - 6,
              top: sy - 6,
              width: sw + 12,
              height: sh + 12,
              borderRadius: 17,
              border: '2px solid rgba(139,92,246,0.55)',
              zIndex: 9998,
              pointerEvents: 'none',
              animation: 'wbSpotPing 1.8s ease-out infinite',
            }}
          />

          {/* Pulse ring 2 (offset phase) */}
          <div
            style={{
              position: 'fixed',
              left: sx - 10,
              top: sy - 10,
              width: sw + 20,
              height: sh + 20,
              borderRadius: 21,
              border: '1.5px solid rgba(99,102,241,0.3)',
              zIndex: 9998,
              pointerEvents: 'none',
              animation: 'wbSpotPing 1.8s ease-out 0.6s infinite',
            }}
          />

          {/* Corner brackets */}
          <CornerBrackets rect={spotRect} />
        </>
      )}

      {/* ── Tooltip card ── */}
      {ready && (
        <div style={getTooltipStyle()}>
          <div
            className="rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)' }}
          >
            {/* Caret arrow */}
            <Caret side={isWelcome ? 'center' : currentStep.tooltipSide} />

            {/* Progress bar top strip */}
            <div className="h-1 bg-gray-800">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-500"
                style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
              />
            </div>

            {/* Body */}
            <div className="bg-gray-900 px-5 pt-4 pb-5">
              {/* Header row */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.3), rgba(6,182,212,0.2))', border: '1px solid rgba(139,92,246,0.35)' }}
                  >
                    <i className={`${currentStep.icon} text-violet-300 text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-violet-400 leading-none mb-0.5">
                      Step {stepIndex + 1} of {STEPS.length}
                    </p>
                    <h3 className="text-white font-bold text-sm leading-tight">{currentStep.title}</h3>
                  </div>
                </div>
                <button
                  onClick={handleDone}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/10 transition-all cursor-pointer flex-shrink-0 ml-2 mt-0.5"
                  title="Close tour"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>

              {/* Description */}
              <p className="text-gray-400 text-xs leading-relaxed mb-4">{currentStep.desc}</p>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-1.5 mb-4">
                {STEPS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setReady(false); setStepIndex(i); }}
                    className={`rounded-full transition-all cursor-pointer ${
                      i === stepIndex
                        ? 'w-5 h-1.5 bg-violet-400'
                        : i < stepIndex
                          ? 'w-1.5 h-1.5 bg-violet-600/60'
                          : 'w-1.5 h-1.5 bg-gray-700'
                    }`}
                  />
                ))}
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                {stepIndex === 0 ? (
                  <button
                    onClick={handleDone}
                    className="flex-1 py-2 text-xs font-medium text-gray-500 hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-white/5"
                  >
                    Skip tour
                  </button>
                ) : (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-1.5 py-2 px-3 text-xs font-medium text-gray-400 hover:text-gray-200 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-white/5"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    Back
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-4 rounded-xl text-xs font-bold text-white transition-all cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #0891b2)' }}
                >
                  {isLast ? (
                    <>
                      <i className="ri-check-line"></i>
                      Got it!
                    </>
                  ) : (
                    <>
                      {stepIndex === 0 ? 'Start tour' : 'Next'}
                      <i className="ri-arrow-right-s-line"></i>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
